/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { ID } from "@smartsuite/smartsuite-client";
import type { ActivityRecord } from "../../../stores/jobs.store";

import * as R from "remeda";
import { useEngineClient } from "../../../services/engineClient";
import { useRootStore } from "../../../stores/hooks";
import { jobsToRecords } from "../ActivityGrid/ActivityGrid.helpers";

import type { ScrollPosition } from "@smartsuite/grid/lib/hooks/useScrollPosition";
import { observer } from "mobx-react-lite";
import type { ActionCandidate } from "../../../types/candidate";
import { ActivityDetailsView } from "../ActivityDetailsView/ActivityDetailsView";
import { AutomationActivityGridView } from "../AutomationActivityGridView/AutomationActivityGridView";
import { StyledWrapper } from "./style";
import { useLocation, useNavigate } from "react-router-dom";
import { useJobRetry } from "../job-retry.hook";
import isEmpty from "lodash/isEmpty";

interface AutomationActivityViewProps {
  sidebarWidth: number;
  goBack: () => void;
  jobId?: ID;
}

const PAGE_SIZE = 100;

export const AutomationActivityView = observer<AutomationActivityViewProps>(
  ({ sidebarWidth, goBack, jobId }) => {
    const {
      config: { userPreferences, workspace },
      automations: { candidate },
      jobsStore,
      jobDetails,
    } = useRootStore();

    const { isJobCanBeRetried } = useJobRetry();

    const automationId = candidate.self.automationId;
    const engineClient = useEngineClient();
    const navigate = useNavigate();
    const [isDetailsPage, setIsDetailsPage] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    const headerRef = useRef<HTMLDivElement>(null);
    const [pageToken, setPageToken] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [timeoutId, setTimeoutId] = useState<
      NodeJS.Timeout | string | number
    >();
    const isMounted = useRef(false);
    const location = useLocation();

    const isNewAutomation = useMemo(() => {
      return location.pathname === "/create";
    }, [location.pathname]);

    useEffect(() => {
      setIsLoading(jobDetails.loading);
    }, [jobDetails.loading]);

    useEffect(() => {
      if (jobId !== undefined) {
        setIsDetailsPage(true);
        handleOnDetails(jobId, true);
      }
    }, [jobId]);

    const loadMoreJobs = (): void => {
      // its not clear why this is happening, but it is happening
      if (isEmpty(automationId)) {
        return;
      }
      setIsLoading(true);
      void engineClient
        .getJobList({ automationId, pageSize: PAGE_SIZE, pageToken })
        .then(({ jobs, nextPageToken }) => {
          const allJobs = jobsStore.jobs.concat(
            jobsToRecords(jobs, isJobCanBeRetried),
          );
          const uniqueJobs: ActivityRecord[] = Array.from(
            new Set(allJobs.map((a) => a.id)),
          )
            .map((id) => allJobs.find((a) => a.id === id))
            .filter((a) => a !== undefined);

          jobsStore.setJobs(uniqueJobs);
          setIsLoading(false);
          setPageToken(nextPageToken);
        });
    };

    const updateLoadedJobs = (
      existingJobs: ActivityRecord[],
      loadedJobs: ActivityRecord[],
    ): {
      jobs: ActivityRecord[];
      loadMore: boolean;
    } => {
      const newJobs: ActivityRecord[] = [];
      let loadMore = false;
      let jobsResult = [...existingJobs];
      let jobIndex = 0;
      for (const job of loadedJobs) {
        const foundIndex = jobsResult.findIndex((item) => item.id === job.id);
        if (foundIndex === -1) {
          newJobs.push(job);
        } else {
          if (
            jobIndex === PAGE_SIZE - 1 &&
            job.status !== jobsResult[foundIndex].status
          ) {
            loadMore = true;
          }
          jobsResult[foundIndex] = job;
        }
        jobIndex++;
      }
      if (newJobs.length > 0) {
        jobsStore.setNewJobs([...jobsStore.newJobs, ...newJobs]);
      }
      if (newJobs.length === PAGE_SIZE) {
        loadMore = true;
      } else if (jobsStore.newJobs.length !== 0) {
        jobsResult = [...jobsStore.newJobs, ...jobsResult];
        jobsStore.setNewJobs([]);
      }
      return {
        jobs: jobsResult,
        loadMore,
      };
    };

    const loadJobsLiveUpdateBatch = (pageToken: string): void => {
      // its not clear why this is happening, but it is happening
      if (isEmpty(automationId)) {
        return;
      }
      void engineClient
        .getJobList({ automationId, pageSize: PAGE_SIZE, pageToken })
        .then(({ jobs, nextPageToken }) => {
          const result = updateLoadedJobs(
            jobsStore.jobs,
            jobsToRecords(jobs, isJobCanBeRetried),
          );
          jobsStore.setJobs(result.jobs);
          if (result.loadMore) {
            loadJobsLiveUpdateBatch(nextPageToken);
          } else {
            if (isMounted.current && !isDetailsPage) {
              startLiveReload();
            }
          }
        });
    };

    const startLiveReload = (): void => {
      setTimeoutId(
        setTimeout(() => {
          loadJobsLiveUpdateBatch("");
        }, 10000),
      );
    };

    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
      };
    }, [timeoutId]);

    // Clear timer of reload if details page is opened
    useEffect(() => {
      if (isDetailsPage && timeoutId !== undefined) {
        clearTimeout(timeoutId);
      } else {
        startLiveReload();
      }

      // Guard the sync-URL navigate: on a direct deep-link mount the
      // candidate store hasn't loaded yet, so automationId is "" and the
      // template collapses to "//history" -- a network-path URL the browser
      // resolves to host "history" (full page navigation off the app).
      if (!isDetailsPage && !isEmpty(automationId)) {
        navigate({
          pathname: `/${automationId}/history`,
          search: location.search,
        });
      }
    }, [isDetailsPage]);

    useEffect(() => {
      if (
        jobsStore.jobs.length < PAGE_SIZE &&
        pageToken.replace("old-", "") !== ""
      ) {
        loadMoreJobs();
      }
    }, [pageToken]);

    useEffect(() => {
      if (R.isTruthy(automationId)) {
        jobsStore.setReload(true);
        loadMoreJobs();
      }

      if (isNewAutomation) {
        jobsStore.setJobs([]);
      }
    }, [automationId, engineClient, isNewAutomation]);

    useLayoutEffect(() => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    }, [headerHeight, headerRef]);

    const onScroll = (
      position: ScrollPosition,
      scrollContentSize: {
        height: number;
        width: number;
      },
    ): void => {
      if (
        !isLoading &&
        pageToken !== "" &&
        position.top > scrollContentSize.height / 4
      ) {
        loadMoreJobs();
      }
    };

    useEffect(() => {
      // We need to show loader until jobDetails is loaded and until all ActionDescriptions are loaded
      if (isLoading && isAllDynamicDescriptionsLoaded()) {
        setIsLoading(false);
      }
    }, [candidate.allActions]);

    const handleGoBack = useCallback(() => {
      setIsDetailsPage(false);
    }, []);

    const isAllDynamicDescriptionsLoaded = (): boolean =>
      candidate.allActions.every((action: ActionCandidate) => {
        return action.description.actionId !== "";
      });

    const onDetails = (jobId: ID): void => {
      navigate({
        pathname: `/${String(automationId)}/history/${jobId}`,
        search: location.search,
      });
    };

    const handleOnDetails = (jobId: ID, skipCheck = false): void => {
      // check if pending job
      const record = jobsStore.jobs.find((record) => record.id === jobId);
      if (
        (record === undefined || record.status === "is_pending") &&
        !skipCheck
      ) {
        return;
      }
      setIsLoading(true);
      jobDetails.setLoading(true);
      void engineClient
        .getJobDetails({
          jobId: String(jobId),
        })
        .then(({ job, recordJobData }) => {
          if (job !== undefined) {
            jobDetails.setJobDetails(job, recordJobData);
            setIsDetailsPage(true);
          }
        })
        .finally(() => {
          jobDetails.setLoading(false);
        });
    };

    return (
      <StyledWrapper sidebarWidth={sidebarWidth}>
        {isDetailsPage && jobDetails !== undefined ? (
          <ActivityDetailsView
            workspace={workspace}
            onGoBack={handleGoBack}
            isLoading={isLoading}
          />
        ) : (
          <AutomationActivityGridView
            candidate={candidate}
            userPreferences={userPreferences}
            workspace={workspace}
            isLoading={isLoading}
            onScroll={onScroll}
            onDetails={onDetails}
            onGoBack={goBack}
          />
        )}
      </StyledWrapper>
    );
  },
);
